import { DatePipe, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircle, faExpand, faFolderClosed, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { AvmDatePipe } from '@hub4b/pipes/avm-date.pipe';
import { NgxSpinnerModule } from 'ngx-spinner';
import { takeUntil } from 'rxjs/operators';
import { BaseDirective } from 'src/app/core/directives/base.directive';
import { TimeConversionHelper } from 'src/app/helpers/time-conversion-helper';
import { NumberKeyDictionary } from 'src/app/models/core/number-key-dictionary_T';
import { DeadlineStates } from 'src/app/models/enums/deadline-state';
import { PropertyType } from 'src/app/models/enums/property-type';
import { Rights } from 'src/app/models/server/enums/rights';
import { Folder } from 'src/app/models/server/folder';
import { FolderWorkflowStepsSnapshot } from 'src/app/models/server/folder-workflow-steps-snapshot';
import { WorkflowStepDefinition } from 'src/app/models/server/workflow-step-definition';
import { WorkflowStepDeadlineStatus } from 'src/app/models/workflow-step-deadline-status';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { WorkflowSectionEventsService } from 'src/app/services/events/workflow-section-events.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { DetailBaseDatasource } from '../../../datasources/detail-base-datasource';
import { WorkflowContextMenuItem } from './workflow-context-menu/models/workflow-context-menu-item';
import { WorkflowContextMenuItemEvent } from './workflow-context-menu/models/workflow-context-menu-item-event';
import { WorkflowContextMenuComponent } from './workflow-context-menu/workflow-context-menu.component';
import { WorkflowStepComponent } from './workflow-step/workflow-step.component';

@Component({
    selector: 'app-workflow-section',
    templateUrl: './workflow-section.component.html',
    styleUrls: ['./workflow-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [WorkflowStepComponent, MatTooltipModule, FaIconComponent, NgxSpinnerModule, WorkflowContextMenuComponent, NgStyle, AvmDatePipe]
})
export class WorkflowSectionComponent extends BaseDirective implements OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    public faExpand = faExpand;
    public deadlineStatusIcon = faCircle;
    public stepCompletionIcon = faFolderClosed;
    public stepActivationIcon = faFolderOpen;

    /**
     * Références des Enums utilisées dans le template.
     */
    public PropertyType = PropertyType;
    public DeadlineStates = DeadlineStates;

    /**
     * Liste des définitions des étapes du workflow.
     */
    public _workflowStepDefinitions: WorkflowStepDefinition[];

    /**
     * Spécifie la liste des définitions des étapes du workflow.
     */
    @Input()
    public set workflowStepDefinitions(value: WorkflowStepDefinition[]) {
        this._workflowStepDefinitions = value;

        this.buildWorkflowStepDeadlineStatus();
    }

    /**
     * Obtient la liste des définitions des étapes du workflow.
     */
    public get workflowStepDefinitions(): WorkflowStepDefinition[] {
        return this._workflowStepDefinitions;
    }

    /**
     * Valeur indiquant si la colonne date de changement d'état est visible.
     */
    @Input()
    public isDateVisible: boolean;

    /**
     * Instance du dossier pour lequel les étapes de workflow sont affichées.
     */
    @Input()
    public folder: Folder;

    /**
     * Liste des étapes de workflow du dossier à afficher.
     */
    private _workflowStepsSnapshot: FolderWorkflowStepsSnapshot[];

    /**
     * Spécifie la liste des étapes de workflow du dossier à afficher.
     */
    @Input()
    public set workflowStepsSnapshot(value: FolderWorkflowStepsSnapshot[]) {
        this._workflowStepsSnapshot = value;

        this.stepSnapshots = new NumberKeyDictionary<FolderWorkflowStepsSnapshot>();

        if (value) {
            value.forEach((x) => {
                this.stepSnapshots[x.workflowStepId] = x;
            });

            this.buildWorkflowStepDeadlineStatus();
        }
    }

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Reference au menu contextuel
     */
    @ViewChild(WorkflowContextMenuComponent)
    private contextMenu: WorkflowContextMenuComponent;

    /**
     * Dictionnaire stockant la correspondance entre l'id d'une étape de workflow et son status 'Date limite'.
     */
    public workflowStepDeadlineStatus: NumberKeyDictionary<WorkflowStepDeadlineStatus> = new NumberKeyDictionary<WorkflowStepDeadlineStatus>();

    /**
     * Dictionaire stockant la correspondance entre l'id d'une étape de workflow et son snapshot.
     */
    public stepSnapshots: NumberKeyDictionary<FolderWorkflowStepsSnapshot>;

    /**
     * Liste des éléments du menu contextuel.
     */
    public contextMenuItems: Array<WorkflowContextMenuItem> = [];

    /**
     * Valeur indiquant si l'utilisateur connecté possède el droit d'activer une étape de workflow.
     */
    public workflowStepActivationRight: boolean = false;

    /**
     * Valeur indiquant si l'utilisateur connecté possède el droit de cloturer une étape de workflow.
     */
    public workflowStepCompletionRight: boolean = false;

    /**
     * Position sur l'axe X de la souris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    private rightClickMenuPositionX: number;

    /**
     * Position sur l'axe Y de la souris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    private rightClickMenuPositionY: number;

    /**
     * Constructeur.
     * @param datasource
     * @param connectedUserStateService
     * @param workflowSectionEventsService
     * @param spinner
     * @param datePipe
     * @param cdr
     */
    constructor(
        private datasource: DetailBaseDatasource,
        private connectedUserStateService: ConnectedUsersStateService,
        private workflowSectionEventsService: WorkflowSectionEventsService,
        private spinner: SpinnerService,
        public datePipe: DatePipe,
        private cdr: ChangeDetectorRef
    ) {
        super();
        this.workflowStepActivationRight = this.connectedUserStateService.hasRight(Rights.WorkflowStepActivation);
        this.workflowStepCompletionRight = this.connectedUserStateService.hasRight(Rights.WorkflowStepCompletion);
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.workflowSectionEventsService.needRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((refreshEvent) => {
            if (!!refreshEvent && refreshEvent.folderId == this.folder.id) {
                this.workflowStepsSnapshot = refreshEvent.workflowStepsSnapshot;
                this.cdr.detectChanges();
            }
        });
    }

    /**
     * Appelée lorsque l'utilisateur modifie l'état d'une étape du workflow.
     * @param workflowsStep : Définition de l'étape de workflow modifiée.
     * @param newStateId : Id du nouvel état de l'étape.
     */
    public onUpdateFolderWorkflowStepState(workflowsStep: WorkflowStepDefinition, newStateId: number): void {
        var workflowStepsSnapshot = this._workflowStepsSnapshot.find((x) => x.workflowStepId == workflowsStep.id);

        if (workflowStepsSnapshot?.currentStateId != newStateId) {
            this.datasource.updateFolderWorkflowStepState(this.folder.viewId, this.folder.id, workflowStepsSnapshot, newStateId);
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique pour ouvrir le menu contextuel.
     * @param event : Evenement émis par le clic droit de la souris.
     * @param stepDefinition : Instance de la définition de l'étape de workflow.
     * @param snapshot : Instance du snapshot de l'étape de workflow.
     * @returns Les éléments du menu contextuel.
     */
    public onContextMenuClick(event: MouseEvent, stepDefinition: WorkflowStepDefinition, snapshot: FolderWorkflowStepsSnapshot) {
        this.contextMenuItems = this.getContextMenuItems(stepDefinition, snapshot);

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        this.contextMenu.openMenu(event);
    }

    /**
     * Levé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement clic du menu contextuel.
     */
    public onContextMenuItemClick(event: WorkflowContextMenuItemEvent): void {
        switch (event.menuItemId) {
            case 'WorkflowStepActivation':
                this.activateFolderWorkflowStep(event.menuItemData);
                break;
            case 'WorkflowStepCompletion':
                this.completeFolderWorkflowStep(event.menuItemData);
                break;
        }
    }

    /**
     * Retourne le style du menu contextuel utilisé pour positionner le menu contextuel.
     * @returns : Le style du menu contextuel.
     */
    public getContextMenuStyle(): any {
        // Positionne le menu contextuel au niveau du pointeur de la souris.
        return {
            position: 'fixed',
            left: `${this.rightClickMenuPositionX}px`,
            top: `${this.rightClickMenuPositionY}px`,
        };
    }

    /**
     * Retourne les informations liées au status de l'étape par rapport à la 'Date limite' d'une étape de workflow d'un dossier.
     * @param stepSnapshot : Snapshot de l'étape de workflow d'un dossier.
     * @returns Infos sur le status.
     */
    private getStepDeadlineStatusInfos(stepSnapshot: FolderWorkflowStepsSnapshot): WorkflowStepDeadlineStatus {
        var stepDefinition = this.workflowStepDefinitions.find((y) => y.id === stepSnapshot.workflowStepId);

        if (stepDefinition.maxDuration > 0 && stepSnapshot.stepStartDate) {
            var remainingTimeBeforeDeadline = this.getRemainingTimeBeforeDeadline(stepDefinition, stepSnapshot);

            var workflowStepDeadlineStatus = new WorkflowStepDeadlineStatus(remainingTimeBeforeDeadline, stepDefinition.maxDuration, stepSnapshot.completed);

            return workflowStepDeadlineStatus;
        } else {
            return WorkflowStepDeadlineStatus.Undefined;
        }
    }

    /**
     * Retourne le nombre de minutes restantes avant que l'étape atteigne la date limite.
     * @param stepDefinition : Instance de la définition de l'étape du workflow.
     * @param stepSnapshot : Snapshot de l'étape de workflow d'un dossier.
     * @returns Un nombre de minutes.
     */
    private getRemainingTimeBeforeDeadline(stepDefinition: WorkflowStepDefinition, stepSnapshot: FolderWorkflowStepsSnapshot): number {
        let stepDuration: number;

        if (stepSnapshot.completed) {
            // Pour une étape complétée, la durée de l'étape correspond à la date de dernier changement d'état de l'étape - la date de début de l'étape.
            stepDuration = Date.parse(stepSnapshot.currentStateDate as any).valueOf() - Date.parse(stepSnapshot.stepStartDate as any).valueOf();
        } else {
            // Pour une étape en cours, la durée de l'étape correspond à la date du jour - la date de début de l'étape.
            stepDuration = Date.now().valueOf() - Date.parse(stepSnapshot.stepStartDate as any).valueOf();
        }

        // Le temps restant avant la date limite correspond à la durée maximale paramétrée pour l'étape - la durée de l'étape.
        var remainingTimeBeforeDeadline = stepDefinition.maxDuration - TimeConversionHelper.convertMillisecondsInMinuts(stepDuration);
        return remainingTimeBeforeDeadline;
    }

    /**
     * Construit les infos sur l'état 'Date limite' des étapes de workflow d'un dossier.
     */
    private buildWorkflowStepDeadlineStatus(): void {
        if (this._workflowStepDefinitions && this._workflowStepsSnapshot) {
            this.spinner.show('workflow-spinner');
            this._workflowStepsSnapshot.forEach((x) => {
                this.workflowStepDeadlineStatus[x.workflowStepId] = this.getStepDeadlineStatusInfos(x);
            });

            this.spinner.hide('workflow-spinner');
        }
    }

    private getContextMenuItems(data: WorkflowStepDefinition, snapshot: FolderWorkflowStepsSnapshot) {
        let menuItems = [];

        if (this.readonly === false) {
            if (snapshot) {
                if (snapshot.completed === true) {
                    menuItems.push(new WorkflowContextMenuItem("Activer l'étape", 'WorkflowStepActivation', data, this.stepActivationIcon, this.workflowStepActivationRight));
                } else {
                    menuItems.push(new WorkflowContextMenuItem("Clôre l'étape", 'WorkflowStepCompletion', data, this.stepCompletionIcon, this.workflowStepCompletionRight));
                }
            } else {
                menuItems.push(new WorkflowContextMenuItem("Activer l'étape", 'WorkflowStepActivation', data, this.stepActivationIcon, this.workflowStepActivationRight));
            }
        }

        return menuItems;
    }

    /**
     * Active une étape de workflow d'un dossier.
     * @param workflowStepsSnapshot : Instance de l'étape du workflow.
     */
    private activateFolderWorkflowStep(workflowsStep: WorkflowStepDefinition): void {
        this.datasource.activateFolderWorkflowStep(this.folder.viewId, this.folder.id, workflowsStep.id);
    }

    /**
     * Finalise une étape de workflow d'un dossier.
     * @param workflowStepsSnapshot : Instance de l'étape du workflow.
     */
    private completeFolderWorkflowStep(workflowsStep: WorkflowStepDefinition): void {
        var workflowStepsSnapshot = this._workflowStepsSnapshot.find((x) => x.workflowStepId == workflowsStep.id);
        this.datasource.completeFolderWorkflowStep(this.folder.viewId, this.folder.id, workflowStepsSnapshot);
    }
}
